import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import "./GraphFromCode.css";
import apiClient from "../utils/apiClient";
import ReactMarkdown from "react-markdown";
import { FaCircleInfo } from "react-icons/fa6";

export default function GraphFromCode() {
  const { t } = useTranslation();
  const [graphPreview, setGraphPreview] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileType, setFileType] = useState("code");
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState(""); // Message de progression du backend
  const [explanation, setExplanation] = useState("");
  const [showExplanation, setShowExplanation] = useState(true);
  const [importMode, setImportMode] = useState("smart");
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [analysisLevel, setAnalysisLevel] = useState("advanced"); // nouveau state
  const [projectName, setProjectName] = useState(""); // nom du projet pour grouper les nœuds
  const [entityTypes, setEntityTypes] = useState({ // nouveau state
    services: true,
    apis: true,
    databases: true,
    middleware: true,
    config: true,
    workflows: true,
    dependencies: true,
    architecture: true
  });
  const [analysisMetadata, setAnalysisMetadata] = useState(null); // nouveau state pour métadonnées
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Formats de fichiers acceptés
  const acceptedFormats = {
    code: [
      ".js", ".ts", ".jsx", ".tsx", ".py", ".java", ".go", ".php", ".cs", ".json", ".html", ".css"
    ],
    docs: [".pdf", ".txt", ".md"],
  };

  // 🚫 NOUVEAUX FILTRES D'EXCLUSION
  const excludedDirectories = [
    "node_modules",
    ".git",
    ".next",
    "dist",
    "build",
    "out",
    "coverage",
    ".vscode",
    ".idea",
    "vendor",
    "__pycache__",
    ".pytest_cache",
    "venv",
    "env",
    ".env",
    "target", // Java/Rust
    "bin",
    "obj", // .NET
    ".gradle", // Gradle
    "logs",
    "tmp",
    "temp"
  ];

  const excludedFiles = [
    ".DS_Store",
    "Thumbs.db",
    ".gitignore",
    ".env.local",
    ".env.development",
    ".env.production",
    "package-lock.json",
    "yarn.lock",
    "pnpm-lock.yaml",
    "composer.lock",
    "Pipfile.lock",
    ".log"
  ];

  // 🚫 EXTENSIONS BINAIRES À EXCLURE (images, vidéos, archives, etc.)
  const excludedExtensions = [
    // Images
    ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg", ".ico", ".webp", ".tiff", ".tif", ".heic", ".avif",
    // Vidéos
    ".mp4", ".avi", ".mov", ".wmv", ".flv", ".mkv", ".webm", ".m4v", ".mpg", ".mpeg",
    // Audio
    ".mp3", ".wav", ".ogg", ".m4a", ".flac", ".aac", ".wma",
    // Archives
    ".zip", ".rar", ".7z", ".tar", ".gz", ".bz2", ".xz", ".iso", ".dmg",
    // Binaires compilés
    ".exe", ".dll", ".so", ".dylib", ".bin", ".app", ".deb", ".rpm",
    // Fonts
    ".ttf", ".otf", ".woff", ".woff2", ".eot",
    // Documents bureautiques (souvent binaires)
    ".docx", ".xlsx", ".pptx", ".doc", ".xls", ".ppt",
    // Bases de données
    ".db", ".sqlite", ".sqlite3", ".mdb",
    // Autres
    ".pdf", ".sketch", ".fig", ".xd", ".psd", ".ai"
  ];

  // 🎯 FONCTION DE FILTRAGE DES FICHIERS
  const shouldExcludeFile = (file) => {
    const filePath = file.webkitRelativePath || file.name;
    const fileName = file.name;
    const fileExt = '.' + fileName.split('.').pop().toLowerCase();

    // Exclure les fichiers cachés (commençant par .)
    if (fileName.startsWith('.') && !fileName.endsWith('.js') && !fileName.endsWith('.ts')) {
      return true;
    }

    // 🚫 Exclure les fichiers binaires (images, vidéos, archives, etc.)
    if (excludedExtensions.includes(fileExt)) {
      return true;
    }

    // Exclure les dossiers interdits
    const pathParts = filePath.split('/');
    for (const part of pathParts) {
      if (excludedDirectories.includes(part)) {
        return true;
      }
    }

    // Exclure les fichiers spécifiques
    if (excludedFiles.some(excluded => fileName.includes(excluded))) {
      return true;
    }

    // Exclure les fichiers trop volumineux (> 1MB)
    if (file.size > 1024 * 1024) {
      return true;
    }

    return false;
  };

  // Fonctions utilitaires
  const getAcceptString = () => {
    const formats = fileType === "pdf" ? acceptedFormats.docs : acceptedFormats.code;
    return formats.join(",");
  };

  const isValidFileFormat = (file) => {
    const extension = `.${file.name.split(".").pop().toLowerCase()}`;
    const validExtensions = fileType === "pdf" ? acceptedFormats.docs : acceptedFormats.code;
    return validExtensions.includes(extension);
  };

  const handleFilesUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validExtensions = fileType === "pdf" ? acceptedFormats.docs : acceptedFormats.code;

    // 🎯 FILTRAGE INTELLIGENT DES FICHIERS
    let filteredFiles = selectedFiles;

    // 1. Filtrer les formats valides
    const invalidFiles = filteredFiles.filter((file) => !isValidFileFormat(file));
    filteredFiles = filteredFiles.filter((file) => isValidFileFormat(file));

    // 2. Filtrer les fichiers/dossiers exclus (node_modules, etc.)
    const excludedFilesFromFilter = filteredFiles.filter((file) => shouldExcludeFile(file));
    filteredFiles = filteredFiles.filter((file) => !shouldExcludeFile(file));

    // � Log des exclusions binaires pour debug
    const binaryFilesExcluded = excludedFilesFromFilter.filter(file => {
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      return excludedExtensions.includes(fileExt);
    });
    if (binaryFilesExcluded.length > 0) {
      console.log(`🚫 ${binaryFilesExcluded.length} fichier(s) binaire(s) exclu(s):`, 
        binaryFilesExcluded.slice(0, 10).map(f => f.name)
      );
    }

    // �👇 NOUVEAU - Construire les listes pour affichage
    const rejected = [
      ...invalidFiles.map(file => ({
        name: file.name,
        reason: t('graphFromCode.filesList.unsupportedFormat', { ext: file.name.split('.').pop() }),
        type: 'invalid'
      })),
      ...excludedFilesFromFilter.map(file => ({
        name: file.name,
        reason: t('graphFromCode.filesList.excludedFile'),
        type: 'excluded'
      }))
    ];

    const accepted = filteredFiles.map(file => ({
      name: file.name,
      size: file.size
    }));

    setAcceptedFiles(accepted);
    setRejectedFiles(rejected);

    // 3. Informer l'utilisateur des exclusions
    let messagesParts = [];
    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.slice(0, 5).map((f) => f.name).join(", ");
      const ellipsis = invalidFiles.length > 5 ? '...' : '';
      messagesParts.push(t('graphFromCode.unsupportedFormats', {
        count: invalidFiles.length,
        names: invalidNames,
        ellipsis
      }));
    }

    if (excludedFilesFromFilter.length > 0) {
      const excludedNames = excludedFilesFromFilter.slice(0, 5).map((f) => f.name).join(", ");
      const ellipsis = excludedFilesFromFilter.length > 5 ? '...' : '';
      messagesParts.push(t('graphFromCode.excludedFiles', {
        count: excludedFilesFromFilter.length,
        names: excludedNames,
        ellipsis
      }));
    }

    if (messagesParts.length > 0) {
      const acceptedCount = filteredFiles.length;
      toast.info(t('graphFromCode.filteringResult', {
        acceptedCount,
        messages: messagesParts.join('\n'),
        validExtensions: validExtensions.join(", ")
      }));
    }

    setFiles(filteredFiles);
  };

  const handleAnalyzeFiles = async () => {
    if (files.length === 0) return;
    
    // ✅ Validation: Nom du projet obligatoire pour les analyses de code
    if (fileType === "code" && !projectName.trim()) {
      toast.error(t('graphFromCode.projectNameRequired', { 
        defaultValue: 'Veuillez saisir un nom de projet' 
      }));
      return;
    }
    
    setLoading(true);
    setAnalysisProgress(0);

    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));

    try {
      const res = await apiClient.post("/upload-files", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const endpoint = fileType === "code" ? "/generate-graph-from-code" : "/generate-graph-from-pdf";

      const response = await apiClient.post(endpoint, {
        files: res.data.files,
        analysisLevel: analysisLevel,
        entityTypes: entityTypes,
        projectName: projectName || undefined, // Nom du projet optionnel
        lang: t.language || 'fr'
      });

      // 🚀 Mode async avec polling (backend renvoie un jobId en 202)
      if (response.status === 202 && response.data.jobId) {
        const jobId = response.data.jobId;
        setAnalysisProgress(10);
        setProgressMessage(getMotivationalMessage(10, t.language));

        // Polling toutes les 3 secondes jusqu'à complétion
        const pollResult = await new Promise((resolve, reject) => {
          const pollInterval = setInterval(async () => {
            try {
              const pollRes = await apiClient.get(`/analysis-job/${jobId}`);
              const jobData = pollRes.data;

              if (jobData.status === 'processing') {
                const progress = jobData.progress || 20;
                setAnalysisProgress(progress);
                setProgressMessage(jobData.message || getMotivationalMessage(progress, t.language));
                return; // Continuer le polling
              }

              clearInterval(pollInterval);

              if (jobData.status === 'completed') {
                setAnalysisProgress(100);
                resolve(jobData.result);
              } else if (jobData.status === 'error') {
                reject(new Error(jobData.error?.message || 'Erreur lors de l\'analyse'));
              }
            } catch (pollError) {
              clearInterval(pollInterval);
              reject(pollError);
            }
          }, 3000);

          // Timeout global de 5 minutes pour le polling
          setTimeout(() => {
            clearInterval(pollInterval);
            reject(new Error('L\'analyse a dépassé le délai maximum (5 minutes)'));
          }, 5 * 60 * 1000);
        });

        // Traiter le résultat du job
        if (pollResult.hasConflict) {
          // Conflits détectés — transformer le format conflicts[] en graph
          console.log('🔍 [POLL DEBUG] pollResult:', {
            hasConflict: pollResult.hasConflict,
            conflictsLength: pollResult.conflicts?.length,
            graphNodesLength: pollResult.graph?.nodes?.length,
            graphRelationshipsLength: pollResult.graph?.relationships?.length
          });
          
          toast.warn(t('graphFromCode.conflictsDetected', {
            defaultValue: `${pollResult.conflictCount} conflit(s) détecté(s) sur ${pollResult.totalNodes} nœuds`
          }));

          // 🔑 Extraire les nœuds EN CONFLIT depuis conflicts[]
          const nodesFromConflicts = (pollResult.conflicts || []).map(c => c.nodeData || c.newNode).filter(Boolean);
          
          // ✅ Extraire les nœuds SANS CONFLIT depuis graph.nodes
          const nodesWithoutConflict = pollResult.graph?.nodes || [];
          
          console.log('🔍 [COMBINE DEBUG] Nœuds:', {
            nodesFromConflicts: nodesFromConflicts.length,
            nodesWithoutConflict: nodesWithoutConflict.length,
            sampleWithoutConflict: nodesWithoutConflict.slice(0, 5).map(n => n.name),
            sampleTeams: nodesWithoutConflict.slice(0, 5).map(n => ({ name: n.name, team: n.featureTeam }))
          });
          
          // 🎯 COMBINER tous les nœuds (avec et sans conflit)
          const allNodes = [...nodesWithoutConflict, ...nodesFromConflicts];
          
          console.log('🔍 [ALL NODES DEBUG] Total:', {
            allNodesCount: allNodes.length,
            allNodesTeams: allNodes.slice(0, 10).map(n => ({ name: n.name, team: n.featureTeam }))
          });

          // 🔗 Combiner les relations globales du backend + relations des nœuds
          const allRelationships = [...(pollResult.graph?.relationships || [])];
          
          // Extraire les relations depuis les nœuds individuels (tous)
          allNodes.forEach(node => {
            if (node.relationships && Array.isArray(node.relationships)) {
              node.relationships.forEach(rel => {
                if (rel.targetNodeName && rel.relationshipType) {
                  allRelationships.push({
                    nodeName1: node.name,
                    nodeName2: rel.targetNodeName,
                    relationshipType: rel.relationshipType,
                    description: rel.description
                  });
                }
              });
            }
          });

          const graphData = {
            nodes: allNodes,  // ✨ TOUS LES NŒUDS
            relationships: allRelationships,
            conflicts: pollResult.conflicts // Garder les infos de conflit
          };

          setGraphPreview(graphData);
          setExplanation(pollResult.message || "Analyse terminée avec des conflits détectés");
          setAnalysisMetadata(pollResult.analysisMetadata || null);
        } else if (pollResult.graph) {
          setGraphPreview(pollResult.graph);
          setExplanation(pollResult.explanation || "");
          setAnalysisMetadata(pollResult.analysisMetadata || null);
        } else {
          setGraphPreview(pollResult);
          setExplanation("");
          setAnalysisMetadata(null);
        }

        localStorage.setItem("lastGraphAnalysis", JSON.stringify(pollResult));

        // ✅ Désactiver loading APRÈS le polling terminé
        setLoading(false);

      } else {
        // Mode synchrone (fallback pour PDF ou réponse directe)
        setAnalysisProgress(100);

        if (response.data.graph) {
          setGraphPreview(response.data.graph);
          setExplanation(response.data.explanation || "");
          setAnalysisMetadata(response.data.analysisMetadata || null);
        } else {
          setGraphPreview(response.data);
          setExplanation("");
          setAnalysisMetadata(null);
        }

        localStorage.setItem("lastGraphAnalysis", JSON.stringify(response.data));

        // ✅ Désactiver loading après traitement synchrone
        setLoading(false);
      }
    } catch (error) {
      // Gestion de l'expiration du token ou absence de token
      const isAuthError = error.response?.status === 401 ||
        (typeof error.response?.data?.message === 'string' && error.response.data.message.toLowerCase().includes('token'));
      if (isAuthError) {
        localStorage.removeItem('authData');
        localStorage.removeItem('lastGraphAnalysis');
        toast.error(t('graphFromCode.sessionExpired'));
        window.location.href = '/';
        setLoading(false);
        return;
      }

      // Gestion timeout / erreur réseau (CORS bloqué par Heroku 503 ou timeout backend)
      const isTimeout = error.response?.status === 504 ||
        error.response?.data?.code === 'PROCESSING_TIMEOUT' ||
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED';
      if (isTimeout) {
        console.error("Timeout lors de l'analyse:", error);
        toast.error(
          t('graphFromCode.timeoutError',
            { defaultValue: "L'analyse a pris trop de temps. Essayez avec moins de fichiers ou le niveau d'analyse 'basic'." }
          ),
          { autoClose: 10000 }
        );
        setLoading(false);
        return;
      }

      console.error("Erreur lors de l'analyse:", error);
      toast.error(t('graphFromCode.analysisError', { message: error.response?.data?.message || error.message }));
      setLoading(false);
    }
  };

  const startProgressSimulation = () => {
    return setInterval(() => {
      setAnalysisProgress((prev) => {
        if (prev < 90) return prev + Math.random() * 3;
        return prev;
      });
    }, 500);
  };

  // 🎯 Messages motivants selon la progression et la langue
  const getMotivationalMessage = (progress, lang = 'fr') => {
    const isFr = lang === 'fr';

    if (progress < 20) {
      return isFr
        ? "🚀 Démarrage de l'analyse..."
        : "🚀 Starting analysis...";
    }
    if (progress < 40) {
      return isFr
        ? "🔍 Lecture et découpage des fichiers en cours..."
        : "🔍 Reading and chunking files...";
    }
    if (progress < 60) {
      return isFr
        ? "🤖 Analyse avancée par l'IA (plusieurs passages)..."
        : "🤖 Advanced AI analysis (multiple passes)...";
    }
    if (progress < 80) {
      return isFr
        ? "🔗 Construction du graphe de connaissances..."
        : "🔗 Building knowledge graph...";
    }
    if (progress < 95) {
      return isFr
        ? "✨ Finalisation et vérification des conflits..."
        : "✨ Finalizing and checking conflicts...";
    }
    return isFr
      ? "✅ Analyse terminée avec succès !"
      : "✅ Analysis completed successfully!";
  };

  // 🎨 Emoji selon la progression
  const getProgressEmoji = (progress) => {
    if (progress < 20) return '🌱';
    if (progress < 40) return '🌿';
    if (progress < 60) return '🌳';
    if (progress < 80) return '🚀';
    if (progress < 100) return '✨';
    return '🎉';
  };

  const handleImportToGlobal = async () => {
    if (!graphPreview) return;

    console.log('📦 GraphPreview avant import:', {
      nodesCount: graphPreview.nodes?.length || 0,
      relationshipsCount: graphPreview.relationships?.length || 0,
      hasConflicts: !!graphPreview.conflicts
    });

    try {
      // 🔥 Envoyer TOUTES les propriétés des nœuds (metadata, technology, etc.)
      const cleanedNodes = (graphPreview.nodes || []).map(node => {
        const cleanNode = {
          name: String(node.name || ''),
          type: String(node.type || 'Default'),
          description: String(node.description || '')
        };

        // Ajouter toutes les propriétés supplémentaires (metadata, technology, featureTeam, etc.)
        if (node.metadata) cleanNode.metadata = node.metadata;
        if (node.technology) cleanNode.technology = node.technology;
        if (node.featureTeam) cleanNode.featureTeam = node.featureTeam;
        if (node.userImpact) cleanNode.userImpact = node.userImpact;
        if (node.businessValue) cleanNode.businessValue = node.businessValue;

        return cleanNode;
      });

      // 🔗 Combiner les relations globales ET les relations dans les nœuds
      let allRelationships = [...(graphPreview.relationships || [])];

      // Extraire les relations depuis les nœuds individuels
      (graphPreview.nodes || []).forEach(node => {
        if (node.relationships && Array.isArray(node.relationships)) {
          node.relationships.forEach(rel => {
            if (rel.targetNodeName && rel.relationshipType) {
              allRelationships.push({
                nodeName1: node.name,
                nodeName2: rel.targetNodeName,
                relationshipType: rel.relationshipType,
                description: rel.description
              });
            }
          });
        }
      });

      console.log('🔍 Relations avant nettoyage:', {
        globalRelationships: (graphPreview.relationships || []).length,
        nodeRelationships: allRelationships.length - (graphPreview.relationships || []).length,
        total: allRelationships.length,
        sample: allRelationships.slice(0, 5)
      });

      const cleanedRelationships = adaptRelationships(allRelationships);

      console.log('📊 Import vers graph principal:', {
        nodesCount: cleanedNodes.length,
        relationshipsCount: cleanedRelationships.length,
        sampleNodes: cleanedNodes.slice(0, 3).map(n => n.name),
        sampleNodesWithTeam: cleanedNodes.slice(0, 3).map(n => ({ name: n.name, team: n.featureTeam })),
        sampleRelationships: cleanedRelationships.slice(0, 3)
      });

      await apiClient.post("/import-graph", {
        nodes: cleanedNodes,
        relationships: cleanedRelationships,
        importMode
      });

      toast.success(t('graphFromCode.importSuccess'));
      setGraphPreview(null);
      localStorage.removeItem("lastGraphAnalysis");
    } catch (error) {
      // Gestion de l'expiration du token ou absence de token
      const isAuthError = error.response?.status === 401 ||
        (typeof error.response?.data?.message === 'string' && error.response.data.message.toLowerCase().includes('token'));
      if (isAuthError) {
        localStorage.removeItem('authData');
        localStorage.removeItem('lastGraphAnalysis');
        toast.error(t('graphFromCode.sessionExpired'));
        window.location.href = '/';
        return;
      }
      console.error("Erreur lors de l'importation:", error);
      toast.error(t('graphFromCode.importError', { message: error.response?.data?.message || error.message }));
    }
  };

  const handleDeleteLocalGraph = () => {
    setGraphPreview(null);
    setAnalysisMetadata(null);
    localStorage.removeItem("lastGraphAnalysis");
  };

  const adaptRelationships = (rels) => {
    if (!rels || !Array.isArray(rels)) return [];

    const mapped = rels.map((rel) => {
      let sourceRel = rel;

      if (rel && typeof rel === 'object' && rel.constructor !== Object) {
        try {
          sourceRel = JSON.parse(JSON.stringify(rel));
        } catch (e) {
          console.warn("Impossible de sérialiser la relation:", rel);
          sourceRel = {};
        }
      }

      const adapted = {
        nodeName1: sourceRel.nodeName1 || sourceRel.source || sourceRel.from || '',
        nodeName2: sourceRel.nodeName2 || sourceRel.target || sourceRel.to || '',
        relationshipType: sourceRel.relationshipType || sourceRel.type || "RELATED_TO",
      };

      const cleanedRelation = {};
      Object.entries(adapted).forEach(([key, value]) => {
        if ((typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') && value !== '') {
          cleanedRelation[key] = String(value);
        }
      });

      return cleanedRelation;
    }).filter(rel => rel.nodeName1 && rel.nodeName2);

    // 🔑 DÉDUPLICATION des relations
    const seen = new Set();
    const deduped = mapped.filter(rel => {
      const key = `${rel.nodeName1}|${rel.relationshipType}|${rel.nodeName2}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    console.log(`🔗 Relations: ${mapped.length} brutes → ${deduped.length} après dédup`);
    return deduped;
  };

  useEffect(() => {
    const savedAnalysis = localStorage.getItem("lastGraphAnalysis");
    if (savedAnalysis) {
      try {
        const parsedAnalysis = JSON.parse(savedAnalysis);

        // Si format avec conflits, transformer en format graph
        if (parsedAnalysis.hasConflict && parsedAnalysis.conflicts) {
          const nodesFromConflicts = (parsedAnalysis.conflicts || []).map(c => c.nodeData || c.newNode).filter(Boolean);
          const nodesWithoutConflict = parsedAnalysis.graph?.nodes || [];
          
          // 🎯 COMBINER tous les nœuds
          const allNodes = [...nodesWithoutConflict, ...nodesFromConflicts];

          // 🔗 Combiner les relations globales + relations des nœuds
          const allRelationships = [...(parsedAnalysis.graph?.relationships || [])];
          
          allNodes.forEach(node => {
            if (node.relationships && Array.isArray(node.relationships)) {
              node.relationships.forEach(rel => {
                if (rel.targetNodeName && rel.relationshipType) {
                  allRelationships.push({
                    nodeName1: node.name,
                    nodeName2: rel.targetNodeName,
                    relationshipType: rel.relationshipType,
                    description: rel.description
                  });
                }
              });
            }
          });

          const graphData = {
            nodes: allNodes,  // ✨ TOUS LES NŒUDS
            relationships: allRelationships,
            conflicts: parsedAnalysis.conflicts
          };

          setGraphPreview(graphData);
          setExplanation(parsedAnalysis.message || "Analyse chargée avec conflits");
          setAnalysisMetadata(parsedAnalysis.analysisMetadata || null);
        } else if (parsedAnalysis.graph) {
          setGraphPreview(parsedAnalysis.graph);
          setExplanation(parsedAnalysis.explanation || "");
          setAnalysisMetadata(parsedAnalysis.analysisMetadata || null);
        } else {
          setGraphPreview(parsedAnalysis);
          setExplanation("");
          setAnalysisMetadata(null);
        }
      } catch (error) {
        console.error("Erreur lors du chargement de l'analyse sauvegardée:", error);
        localStorage.removeItem("lastGraphAnalysis");
      }
    }
  }, []);

  return (
    <div className="modern-text-creator-container">
      <div id="section-graphFromFiles"></div>

      {/* Header moderne avec style harmonisé */}
      <div className="text-creator-header">
        <div className="creator-summary-card">
          <div className="creator-indicator">
            <span className="creator-icon">{fileType === "code" ? "📁" : "📄"}</span>
            <div className="creator-info">
              <h3 className="creator-title">
                {t('graphFromCode.title', {
                  type: fileType === "code" ? t('graphFromCode.sourceCode') : t('graphFromCode.documents')
                })}
              </h3>
              <p className="creator-description">
                {fileType === "code"
                  ? t('graphFromCode.analyzeCodeDesc')
                  : t('graphFromCode.extractConceptsDesc')}
              </p>
            </div>
          </div>
          <div className="format-status">
            <div className="format-indicator">
              <span className="format-dot"></span>
              <span className="format-text">
                Mode: {fileType === "code" ? "Analyse de code" : "Extraction PDF"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Corps du générateur de graphe */}
      <div className="text-creator-body">
        {/* Sélection du type de fichier */}
        <div className="format-controls-section">
          <div className="format-tabs">
            <button
              onClick={() => setFileType("code")}
              className={`format-tab ${fileType === "code" ? "active" : ""}`}
            >
              <span className="tab-icon">💻</span>
              <span className="tab-label">Code Source</span>
            </button>
            <button
              onClick={() => setFileType("pdf")}
              className={`format-tab ${fileType === "pdf" ? "active" : ""}`}
            >
              <span className="tab-icon">📑</span>
              <span className="tab-label">Documents PDF</span>
            </button>
          </div>

          {/* Instructions spécifiques au type de fichier */}
          <div className="search-hints">
            <div className="hint-section">
              <span className="suggestion-chip">
                <span className="hint-icon">💡</span>
                {fileType === "code"
                  ? t('graphFromCode.selectFilesHint.code')
                  : t('graphFromCode.selectFilesHint.pdf')}
              </span>
            </div>
          </div>
        </div>

        {/* Section d'upload de fichiers */}
        <div className="text-input-section">
          {/* Indication des formats acceptés */}
          <div className="input-header">
            <span className="input-icon">{fileType === "code" ? "📄" : "📄"}</span>
            <span className="input-label">{t('graphFromCode.supportedFormats')}</span>
            <div className="formats-badges">
              {(fileType === "pdf" ? acceptedFormats.docs : acceptedFormats.code).map((format) => (
                <span key={format} className="suggestion-chip">
                  {format}
                </span>
              ))}
            </div>
          </div>

          {/* 🚫 NOUVELLE SECTION: Filtres d'exclusion */}
          {fileType === "code" && (
            <div className="input-header">
              <span className="input-icon">🚫</span>
              <span className="input-label">{t('graphFromCode.autoExcluded')}</span>
              <div className="formats-badges">
                <div className="exclusion-group">
                  <span className="exclusion-title">{t('graphFromCode.folderTypes')}</span>
                  {excludedDirectories.slice(0, 6).map((dir) => (
                    <span key={dir} className="suggestion-chip excluded">
                      {dir}
                    </span>
                  ))}
                  <span className="suggestion-chip excluded">
                    {t('graphFromCode.moreExcluded', { count: excludedDirectories.length - 6 })}
                  </span>
                </div>
                <div className="exclusion-group">
                  <span className="exclusion-title">{t('graphFromCode.fileTypes')}</span>
                  <span className="suggestion-chip excluded">
                    {t('graphFromCode.largeFilesNote')}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="input-container">
            <div className="input-header">
              <span className="input-icon">{fileType === "code" ? "📁" : "📄"}</span>
              <span className="input-label">{t('graphFromCode.selectYourFiles')}</span>
              {files.length > 0 && (
                <span className="char-counter">
                  {files.length} fichier{files.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="input-content">
              <input
                type="file"
                multiple
                accept={getAcceptString()}
                webkitdirectory={fileType === "code" ? "true" : undefined}
                directory={fileType === "code" ? "true" : undefined}
                onChange={handleFilesUpload}
                className="file-input"
              />
            </div>
          </div>

          {/* 👇 NOUVEAU - Compteur total en haut */}
          {(acceptedFiles.length > 0 || rejectedFiles.length > 0) && (
            <div className="files-counter-banner">
              <div className="counter-badge">
                <span className="counter-icon">📦</span>
                <span className="counter-text">
                  {t('graphFromCode.filesCounter.total', { count: acceptedFiles.length + rejectedFiles.length })}
                </span>
              </div>
              <div className="counter-details">
                <span className="detail-item accepted">
                  <span className="detail-icon">✅</span>
                  {acceptedFiles.length} {t('graphFromCode.filesCounter.accepted')}
                </span>
                <span className="detail-item rejected">
                  <span className="detail-icon">⚠️</span>
                  {rejectedFiles.length} {t('graphFromCode.filesCounter.rejected')}
                </span>
              </div>
            </div>
          )}

          {/* 👇 NOUVEAU - Section des fichiers acceptés/refusés */}
          {(acceptedFiles.length > 0 || rejectedFiles.length > 0) && (
            <div className="files-summary-section">
              {/* Fichiers acceptés */}
              {acceptedFiles.length > 0 && (
                <div className="accepted-files-panel">
                  <div className="panel-header">
                    <span className="header-icon">✅</span>
                    <span className="header-title">
                      {t('graphFromCode.filesList.acceptedTitle')} ({acceptedFiles.length})
                    </span>
                  </div>
                  <div className="files-list">
                    {acceptedFiles.slice(0, 10).map((file, idx) => (
                      <div key={idx} className="file-item accepted">
                        <span className="file-icon">📄</span>
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                    ))}
                    {acceptedFiles.length > 10 && (
                      <div className="file-item more">
                        <span className="more-text">
                          {t('graphFromCode.filesList.more', { count: acceptedFiles.length - 10 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Fichiers refusés */}
              {rejectedFiles.length > 0 && (
                <div className="rejected-files-panel">
                  <div className="panel-header">
                    <span className="header-icon">⚠️</span>
                    <span className="header-title">
                      {t('graphFromCode.filesList.rejectedTitle')} ({rejectedFiles.length})
                    </span>
                  </div>
                  <div className="files-list">
                    {rejectedFiles.slice(0, 10).map((file, idx) => (
                      <div key={idx} className="file-item rejected">
                        <span className="file-icon">❌</span>
                        <span className="file-name">{file.name}</span>
                        <span className="file-reason">{file.reason}</span>
                      </div>
                    ))}
                    {rejectedFiles.length > 10 && (
                      <div className="file-item more">
                        <span className="more-text">
                          {t('graphFromCode.filesList.moreRejected', { count: rejectedFiles.length - 10 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}



          {/* 🎯 NOUVELLE SECTION: Options d'analyse */}
          {fileType === "code" && (
            <div className="analysis-options-section">
              <div className="input-header">
                <span className="input-icon">⚙️</span>
                <span className="input-label">{t('graphFromCode.analysisOptions.title')}</span>
              </div>

              {/* Nom du projet */}
              <div className="options-group">
                <label className="option-label">
                  📦 {t('graphFromCode.projectName', { defaultValue: 'Nom du projet' })} 
                  <span style={{ color: '#f44336', marginLeft: '4px' }}>*</span>
                </label>
                <input
                  type="text"
                  className="project-name-input"
                  placeholder={t('graphFromCode.projectNamePlaceholder', { defaultValue: 'Ex: mon-app, backend-api, gwent-companion...' })}
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  maxLength={50}
                  required
                />
                <small className="hint-text">
                  {t('graphFromCode.projectNameHint', { defaultValue: 'Tous les nœuds seront groupés sous ce projet dans le graphe' })}
                </small>
              </div>

              {/* Niveau d'analyse */}
              <div className="options-group">
                <label className="option-label">{t('graphFromCode.analysisOptions.level')}</label>
                <div className="radio-group">
                  <label className={`radio-option ${analysisLevel === 'basic' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="basic"
                      checked={analysisLevel === 'basic'}
                      onChange={(e) => setAnalysisLevel(e.target.value)}
                    />
                    <span className="radio-text">
                      <strong>{t('graphFromCode.analysisOptions.basic')}</strong>
                      <small>{t('graphFromCode.analysisOptions.basicDesc')}</small>
                    </span>
                  </label>

                  <label className={`radio-option ${analysisLevel === 'advanced' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="advanced"
                      checked={analysisLevel === 'advanced'}
                      onChange={(e) => setAnalysisLevel(e.target.value)}
                    />
                    <span className="radio-text">
                      <strong>{t('graphFromCode.analysisOptions.advanced')}</strong>
                      <small>{t('graphFromCode.analysisOptions.advancedDesc')}</small>
                    </span>
                  </label>
                </div>
              </div>

              {/* Types d'entités (seulement pour l'analyse avancée) */}
              {analysisLevel === 'advanced' && (
                <div className="options-group">
                  <label className="option-label">{t('graphFromCode.analysisOptions.entityTypes')}</label>
                  <div className="checkbox-grid">
                    {Object.entries(entityTypes).map(([type, enabled]) => (
                      <label key={type} className={`checkbox-option ${enabled ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => setEntityTypes(prev => ({
                            ...prev,
                            [type]: e.target.checked
                          }))}
                        />
                        <span className="checkbox-text">
                          {t(`graphFromCode.analysisOptions.${type}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Barre de progression améliorée */}
          {loading && (
            <div className="progress-section">
              <div className="progress-header">
                <span className="progress-emoji">{getProgressEmoji(analysisProgress)}</span>
                <span className="progress-percentage">{Math.round(analysisProgress)}%</span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${analysisProgress}%` }}
                >
                  <div className="progress-bar-glow"></div>
                </div>
              </div>
              <p className="progress-message">
                {analysisProgress < 100
                  ? (progressMessage || getMotivationalMessage(analysisProgress, t.language))
                  : (t('graphFromCode.analysisComplete') || '✅ Analyse terminée !')}
              </p>
              {analysisProgress > 0 && analysisProgress < 100 && (
                <p className="progress-hint">
                  {t.language === 'fr'
                    ? "💡 Astuce : L'analyse de fichiers volumineux peut prendre quelques minutes. Merci de votre patience !"
                    : "💡 Tip: Analyzing large files may take a few minutes. Thank you for your patience!"}
                </p>
              )}
            </div>
          )}
          <div className="input-footer">
            <div className="input-tips">
              {t('graphFromCode.chooseTip')}
            </div>
            <button
              onClick={handleAnalyzeFiles}
              disabled={loading || files.length === 0}
              className="analyze-btn"
            >
              <span className="btn-icon">{fileType === "code" ? "🔍" : "📋"}</span>
              <span>
                {loading
                  ? "Analyse en cours..."
                  : fileType === "code"
                    ? t('graphFromCode.analyzeCodeButton')
                    : t('graphFromCode.extractInfoButton')}
              </span>
            </button>
          </div>
        </div>

        {/* Explication du LLM */}
        {explanation && (
          <div className="mentioned-nodes-section">
            <div
              className="section-title"
              onClick={() => setShowExplanation(!showExplanation)}
              style={{ cursor: 'pointer' }}
            >
              <span>🧠</span>
              {t('graphFromCode.aiInterpretation')}
              <span style={{ marginLeft: 'auto', fontSize: '0.8rem' }}>
                {showExplanation ? "▼" : "▶"}
              </span>
            </div>
            {showExplanation && (
              <div className="explanation-content">
                <ReactMarkdown>{explanation}</ReactMarkdown>
              </div>
            )}
          </div>
        )}

        {/* Affichage du graphe */}
        {graphPreview ? (
          <div className="node-preview-section">
            {/* Header du preview */}
            <div className="preview-header">
              <div className="preview-main-info">
                <h3 className="preview-title">{t('graphFromCode.graphSummaryTitle')}</h3>
                <p className="preview-description">
                  Graphe extrait de vos fichiers {fileType === "code" ? "de code" : "PDF"}
                </p>
              </div>
              <div className="preview-badge">{t('graphFromCode.readyToImport')}</div>
            </div>

            {/* 🚀 NOUVELLE SECTION: Métadonnées d'analyse avancée */}
            {analysisMetadata?.level === 'advanced' && analysisMetadata?.architecturalSummary && (
              <div className="analysis-metadata-section">
                <div className="metadata-header">
                  <span className="metadata-icon">🏗️</span>
                  <span className="metadata-title">{t('graphFromCode.results.architecturalInsights')}</span>
                  <div className="analysis-badge advanced">
                    {t('graphFromCode.results.advancedAnalysis')}
                  </div>
                </div>

                <div className="architectural-summary">
                  {analysisMetadata.architecturalSummary.pattern && (
                    <div className="insight-item">
                      <span className="insight-label">Pattern architectural:</span>
                      <span className="insight-value">{analysisMetadata.architecturalSummary.pattern}</span>
                    </div>
                  )}

                  {analysisMetadata.architecturalSummary.complexity && (
                    <div className="insight-item">
                      <span className="insight-label">{t('graphFromCode.results.complexity')}:</span>
                      <span className={`insight-value complexity-${analysisMetadata.architecturalSummary.complexity}`}>
                        {t(`graphFromCode.results.${analysisMetadata.architecturalSummary.complexity}`)}
                      </span>
                    </div>
                  )}

                  {analysisMetadata.architecturalSummary.techStack && (
                    <div className="insight-item">
                      <span className="insight-label">{t('graphFromCode.results.techStackDetected')}:</span>
                      <div className="tech-stack-tags">
                        {analysisMetadata.architecturalSummary.techStack.map((tech, idx) => (
                          <span key={idx} className="tech-tag">{tech}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {analysisMetadata.architecturalSummary.securityLevel && (
                    <div className="insight-item">
                      <span className="insight-label">{t('graphFromCode.results.securityFeatures')}:</span>
                      <span className={`insight-value security-${analysisMetadata.architecturalSummary.securityLevel}`}>
                        {analysisMetadata.architecturalSummary.securityLevel}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contenu principal */}
            <div className="preview-main-info">
              {/* Résumé des nœuds */}
              <div className="info-grid">
                <div className="info-card">
                  <div className="info-header">
                    <span className="info-icon">🎯</span>
                    <span>{t('graphFromCode.nodesDetected')}</span>
                  </div>
                  <div className="info-value">{(graphPreview.nodes || []).length}</div>
                </div>
                <div className="info-card">
                  <div className="info-header">
                    <span className="info-icon">🔗</span>
                    <span>{t('graphFromCode.relationsFound')}</span>
                  </div>
                  <div className="info-value">{(() => {
                    let rels = graphPreview.relationships || [];
                    if ((!rels || rels.length === 0) && Array.isArray(graphPreview.nodes)) {
                      rels = [];
                      graphPreview.nodes.forEach(node => {
                        if (node.relationships && Array.isArray(node.relationships)) {
                          node.relationships.forEach(rel => {
                            if (rel.targetNodeName && rel.relationshipType) {
                              rels.push({
                                nodeName1: node.name,
                                nodeName2: rel.targetNodeName,
                                relationshipType: rel.relationshipType
                              });
                            }
                          });
                        }
                      });
                    }
                    return rels.length;
                  })()}</div>
                </div>
              </div>

              {/* Tableau des nœuds */}
              <div className="mentioned-nodes-section">
                <div className="section-title">
                  <span>📊</span>
                  {t('graphFromCode.nodesOverview')}
                </div>
                <div className="table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Type</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(graphPreview.nodes || []).slice(0, 10).map((node, i) => (
                        <tr key={node.name || i}>
                          <td className="node-name">{node.name}</td>
                          <td><span className="node-type-badge">{node.type}</span></td>
                          <td className="node-description">{node.description?.slice(0, 80) || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(graphPreview.nodes || []).length > 10 && (
                    <div className="table-footer">
                      ...et {(graphPreview.nodes || []).length - 10} autres nœuds
                    </div>
                  )}
                </div>
              </div>

              {/* Section des relations */}
              <div className="relationships-section">
                <div className="relationships-header">
                  <div className="relationships-label">
                    <span>🔗</span>
                    {t('graphFromCode.detectedRelations')}
                  </div>
                  <div className="relationships-count">
                    {(() => {
                      let rels = graphPreview.relationships || [];
                      if ((!rels || rels.length === 0) && Array.isArray(graphPreview.nodes)) {
                        rels = [];
                        graphPreview.nodes.forEach(node => {
                          if (node.relationships && Array.isArray(node.relationships)) {
                            node.relationships.forEach(rel => {
                              if (rel.targetNodeName && rel.relationshipType) {
                                rels.push({
                                  nodeName1: node.name,
                                  nodeName2: rel.targetNodeName,
                                  relationshipType: rel.relationshipType
                                });
                              }
                            });
                          }
                        });
                      }
                      return rels.length;
                    })()}
                  </div>
                </div>
                <div className="relationships-grid2">
                  {(() => {
                    let rels = graphPreview.relationships || [];
                    if ((!rels || rels.length === 0) && Array.isArray(graphPreview.nodes)) {
                      rels = [];
                      graphPreview.nodes.forEach(node => {
                        if (node.relationships && Array.isArray(node.relationships)) {
                          node.relationships.forEach(rel => {
                            if (rel.targetNodeName && rel.relationshipType) {
                              rels.push({
                                nodeName1: node.name,
                                nodeName2: rel.targetNodeName,
                                relationshipType: rel.relationshipType
                              });
                            }
                          });
                        }
                      });
                    }
                    return rels.slice(0, 5).map((rel, i) => (
                      <div key={i} className="relationship-card">
                        <div className="relationship-flow">
                          <span className="source-node">{rel.nodeName1}</span>
                          <span className="relationship-arrow">→</span>
                          <span className="relationship-type">{rel.relationshipType}</span>
                          <span className="relationship-arrow">→</span>
                          <span className="target-node">{rel.nodeName2}</span>
                        </div>
                      </div>
                    ));
                  })()}
                  {(() => {
                    let rels = graphPreview.relationships || [];
                    if ((!rels || rels.length === 0) && Array.isArray(graphPreview.nodes)) {
                      rels = [];
                      graphPreview.nodes.forEach(node => {
                        if (node.relationships && Array.isArray(node.relationships)) {
                          node.relationships.forEach(rel => {
                            if (rel.targetNodeName && rel.relationshipType) {
                              rels.push({
                                nodeName1: node.name,
                                nodeName2: rel.targetNodeName,
                                relationshipType: rel.relationshipType
                              });
                            }
                          });
                        }
                      });
                    }
                    if (rels.length > 5) {
                      return (
                        <div className="relationship-card more-relations">
                          <div className="more-text">
                            ...et {rels.length - 5} autres relations
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* Options d'importation */}
              <div className="metadata-section">
                <div className="metadata-header">
                  <div className="metadata-label">
                    <span>⚙️</span>
                    {t('graphFromCode.importSettings')}
                  </div>
                </div>
                <div className="metadata-content">
                  <div className="form-group">
                    <label>{t('graphFromCode.importStrategy')}</label>
                    <select
                      value={importMode}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="metadata-select"
                    >
                      <option value="smart">{t('graphFromCode.importOptions.smart')}</option>
                      <option value="skip">{t('graphFromCode.importOptions.skip')}</option>
                      <option value="replace">{t('graphFromCode.importOptions.replace')}</option>
                      <option value="merge">{t('graphFromCode.importOptions.merge')}</option>
                    </select>
                    <p className="form-help">
                      <FaCircleInfo style={{ marginRight: '0.5rem' }} />
                      {importMode === "smart" && t('graphFromCode.importModes.smart')}
                      {importMode === "skip" && t('graphFromCode.importModes.skip')}
                      {importMode === "replace" && t('graphFromCode.importModes.replace')}
                      {importMode === "merge" && t('graphFromCode.importModes.merge')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="preview-actions">
              <button
                onClick={handleImportToGlobal}
                className="action-btn primary"
              >
                <span>💾</span>
                Importer dans le graphe global
              </button>
              <button
                onClick={handleDeleteLocalGraph}
                className="action-btn secondary"
              >
                <span>🗑️</span>
                Supprimer ce graphe temporaire
              </button>
            </div>
          </div>
        ) : (
          <div className="mentioned-nodes-section">
            <div className="section-title">
              <span>📊</span>
              Aucun graphe à afficher
            </div>
            <div className="empty-state">
              <p>Lancez une analyse de {fileType === "code" ? "code" : "documents PDF"} pour générer un graphe</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
